import { useState, useEffect } from 'react';
import { 
  User,
  MapPin,
  Home,
  Bed,
  Bath,
  Ruler,
  DollarSign,
  Calendar,
  Eye,
  Clock,
  UserCheck,
  X,
  Loader2,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { Combobox, ComboboxOption } from './ui/combobox';
import { Property } from '../types/property';
import { propertyAPI } from '../lib/property-api';
import { adminUserAPI } from '../lib/admin-api';
import { toast } from 'sonner';

interface PropertyDetailModalProps {
  property: Property | null;
  open: boolean;
  onClose: () => void;
  onUpdated?: () => void;
}

interface Agent {
  id: string;
  name: string;
  email: string;
  groups?: string[];
}

export function PropertyDetailModal({ property, open, onClose, onUpdated }: PropertyDetailModalProps) {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<string>('');
  const [isAssigning, setIsAssigning] = useState(false);

  useEffect(() => {
    if (open && property) {
      loadAgents();
      setSelectedAgentId(property.agentId || '');
    }
  }, [open, property]);

  const loadAgents = async () => {
    setIsLoadingAgents(true);
    try {
      console.log('[PropertyDetailModal] Loading agents...');
      const rawUsers = await adminUserAPI.getUsers();
      console.log('[PropertyDetailModal] Fetched users:', rawUsers);
      // F-019: API may return non-array (object wrapper) — guard before .filter()
      const users = Array.isArray(rawUsers) ? rawUsers : [];

      // Filter to only agents - check BOTH groups array AND role field
      // Groups array: used when user logs in via Cognito (has cognitoGroups)
      // Role field: always present in KV store (like "Agent", "Admin")
      const agentUsers = users.filter((user: any) => {
        const hasAgentGroup = user.groups?.includes('agent') || user.groups?.includes('admin');
        const hasAgentRole = user.role === 'Agent' || user.role === 'agent' || user.role === 'Admin' || user.role === 'admin';
        const hasCognitoGroups = user.cognitoGroups?.includes('agent') || user.cognitoGroups?.includes('admin');
        
        return hasAgentGroup || hasAgentRole || hasCognitoGroups;
      });
      console.log('[PropertyDetailModal] Filtered agents:', agentUsers);
      
      setAgents(agentUsers);
      
      if (agentUsers.length === 0) {
        console.warn('[PropertyDetailModal] No agents found! Check user roles in the database.');
        toast.warning('No agents available. Please create users with "Agent" role first.');
      }
    } catch (err) {
      logger.error('[PropertyDetailModal] Error loading agents', err as Error, undefined, 'PropertyDetailModal');
      toast.error('Failed to load agents');
    } finally {
      setIsLoadingAgents(false);
    }
  };

  const handleAssignAgent = async () => {
    if (!property) return;

    setIsAssigning(true);
    try {
      await propertyAPI.assignAgent(
        property.id, 
        selectedAgentId || null
      );
      
      toast.success(
        selectedAgentId 
          ? 'Agent assigned successfully' 
          : 'Agent unassigned successfully'
      );
      
      onUpdated?.();
      onClose();
    } catch (err: any) {
      logger.error('Error assigning agent', err as Error, undefined, 'PropertyDetailModal');
      toast.error(`Failed to assign agent: ${err.message}`);
    } finally {
      setIsAssigning(false);
    }
  };

  if (!property) return null;

  const formatPrice = (price: number) => {
    if (!price && price !== 0) return '—';
    if (price >= 1000000) {
      return `R${(price / 1000000).toFixed(2)}M`;
    }
    return `R${price.toLocaleString()}`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get the assigned agent details
  const assignedAgent = agents.find(a => a.id === property.agentId);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle className="text-2xl">{property.title}</DialogTitle>
              <DialogDescription className="flex items-center gap-1.5 mt-1">
                <MapPin className="w-4 h-4" />
                {property.address?.suburb || ''}{property.address?.city ? `, ${property.address.city}` : ''}
              </DialogDescription>
            </div>
            <Badge
              variant={
                property.status === 'Published' ? 'default' :
                property.status === 'In-Review' ? 'secondary' :
                property.status === 'Archived' ? 'destructive' : 'outline'
              }
            >
              {property.status}
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Property Images */}
          {property.media && property.media.length > 0 && (
            <div className="grid grid-cols-3 gap-2 rounded-lg overflow-hidden">
              {property.media.slice(0, 6).map((media, i) => (
                <img 
                  key={i}
                  src={media.url} 
                  alt={`${property.title} ${i + 1}`}
                  className={`w-full object-cover rounded-lg ${i === 0 ? 'col-span-2 row-span-2 h-64' : 'h-[124px]'}`}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ))}
            </div>
          )}

          {/* Price */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DollarSign className="w-6 h-6 text-[#FDB022]" />
              <div>
                <p className="text-sm text-muted-foreground">Price</p>
                <p className="text-3xl font-bold text-[#FDB022]">{formatPrice(property.price)}</p>
              </div>
            </div>
            <Badge variant="outline" className="text-sm py-1 px-3">
              {property.type}
            </Badge>
          </div>

          <Separator />

          {/* Property Details Grid */}
          <div>
            <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
              <Home className="w-4 h-4" />
              Property Details
            </h4>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center p-4 rounded-lg bg-muted/50">
                <Bed className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">Bedrooms</p>
                <p className="text-lg font-semibold">{property.facts?.bedrooms || property.bedrooms || 0}</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-muted/50">
                <Bath className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">Bathrooms</p>
                <p className="text-lg font-semibold">{property.facts?.bathrooms || property.bathrooms || 0}</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-muted/50">
                <Ruler className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">Area</p>
                <p className="text-lg font-semibold">{property.facts?.floorSize || property.facts?.erfSize || property.facts?.area || (property as any).squareMeters || (property as any).floorSize || property.area || 0}m²</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-muted/50">
                <Home className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">Type</p>
                <p className="text-sm font-semibold">{property.type}</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Agent Assignment Section */}
          <div>
            <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
              <UserCheck className="w-4 h-4" />
              Agent Assignment
            </h4>
            
            {assignedAgent && (
              <div className="mb-4 p-3 rounded-lg bg-muted/50 border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#FDB022]/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-[#FDB022]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Currently Assigned</p>
                    <p className="text-sm text-muted-foreground">{assignedAgent.name}</p>
                    <p className="text-xs text-muted-foreground">{assignedAgent.email}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <div>
                <Label htmlFor="agent-select">Assign to Agent</Label>
                <Combobox
                  options={[
                    { value: 'unassigned', label: 'Unassigned' },
                    ...agents.map(agent => ({
                      value: agent.id,
                      label: agent.name,
                      description: agent.email
                    }))
                  ]}
                  value={selectedAgentId || 'unassigned'}
                  onValueChange={(value) => setSelectedAgentId(value === 'unassigned' ? '' : value)}
                  placeholder={isLoadingAgents ? 'Loading agents...' : 'Select an agent'}
                  searchPlaceholder="Search agents by name or email..."
                  emptyText="No agents found."
                  disabled={isLoadingAgents || isAssigning}
                />
              </div>

              <Button
                data-testid="property-detail-assign-agent-button"
                onClick={handleAssignAgent}
                disabled={isAssigning || selectedAgentId === (property.agentId || '')}
                className="w-full bg-[#FDB022] hover:bg-[#FDB022]/90 text-black"
              >
                {isAssigning ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Assigning...
                  </>
                ) : (
                  selectedAgentId ? 'Assign Agent' : 'Unassign Agent'
                )}
              </Button>
            </div>
          </div>

          <Separator />

          {/* Property Stats */}
          <div>
            <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Property Statistics
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 mb-1">
                  <Eye className="w-4 h-4 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">Total Views</p>
                </div>
                <p className="text-xl font-semibold">{property.totalViews || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">View Time</p>
                </div>
                <p className="text-xl font-semibold">
                  {property.totalViewTime ? `${Math.round(property.totalViewTime / 60)}m` : '0m'}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">Listed Date</p>
                </div>
                <p className="text-sm font-semibold">{formatDate(property.listingDate || property.createdAt)}</p>
              </div>
            </div>
          </div>

          {/* Description */}
          {property.description && (
            <>
              <Separator />
              <div>
                <h4 className="text-sm font-medium mb-2">Description</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {property.description}
                </p>
              </div>
            </>
          )}

          {/* Metadata */}
          <Separator />
          <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
            <div>
              <span className="font-medium text-foreground">Property ID:</span>{' '}
              <span className="font-mono">{property.id}</span>
            </div>
            {property.somethingPropertyId && (
              <div>
                <span className="font-medium text-foreground">SP ID:</span>{' '}
                <span className="font-mono">{property.somethingPropertyId}</span>
              </div>
            )}
            {property.externalId && (
              <div>
                <span className="font-medium text-foreground">External ID:</span>{' '}
                <span className="font-mono">{property.externalId}</span>
              </div>
            )}
            {(property.externalSource || property.source) && (
              <div>
                <span className="font-medium text-foreground">Source:</span>{' '}
                {property.externalSource || property.source}
              </div>
            )}
            <div>
              <span className="font-medium text-foreground">Created:</span>{' '}
              {formatDate(property.createdAt)}
            </div>
            <div>
              <span className="font-medium text-foreground">Updated:</span>{' '}
              {formatDate(property.updatedAt)}
            </div>
            {property.agent && (
              <div className="col-span-2">
                <span className="font-medium text-foreground">Linked Agent:</span>{' '}
                {property.agent.name} ({property.agent.email})
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}